import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Animated, Image, TouchableOpacity, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { StackNavigationOptions } from '@react-navigation/stack';
import { useIsFocused } from '@react-navigation/native';
import { Text } from '@/components/Text';
import { useSidebarAnimation } from '@/components/ui/therapist/CollapsibleSidebar/SidebarAnimationContext';
import { Button } from '@/components/ui/therapist/Button';
import { Icon } from '@/components/Icon';

import { styles } from './styles';
import colors from '@/config/colors';

export const titlesWithClosedSidebar = [
  'Aufgaben auswählen',
  'Eigentraining',
  'Aufgaben',
  'Aufgaben Exercise',
  'Bilder Anzahl',
];

const titlesWithSidebar = [
  'Patient:innen',
  'Über die App',
  'Sperrbildschirm',
  'Kontakt',
  'Meine Einwilligungen',
  'Benutzerkonto',
  'Patienten-Profil',
  'Bibliothek',
];

const namesWithSidebar = ['PatientProfile'];

const titlesWithoutLogo = [
  'Ergebnisse',
  'Eigentraining einstellen',
  'Aufgabeneinstellungen',
  'Bilder Anzahl',
  'Profil hinzufügen',
  'Profil bearbeiten',
];

// Every step of the set editor retitles itself per flow, so these are matched
// by route name — a title-based list silently loses screens on every rewording.
const namesWithoutLogo = [
  'SetCreation',
  'SetDetails',
  'WordSet',
  'SentenceSet',
  'TextSet',
  'TemplateSelect',
];

const titlesWithoutBackButton = [
  'Kontakt',
  'Eigentraining',
  'Über die App',
  'Sperrbildschirm',
  'Meine Einwilligungen',
  'Benutzerkonto',
  'Bibliothek',
];

export const AnimatedHeader: React.FC<{
  onBack?: () => void;
  title: string;
  name: string;
  options?: StackNavigationOptions & { headerUpsideDown?: boolean };
  replacedTitle?: string;
}> = ({ onBack, title, name, options, replacedTitle }) => {
  const [showChevron, setShowChevron] = useState<boolean>(false);
  const {
    bigNavHeight,
    isOpen,
    headerHeight,
    setAppTitle,
    setHeaderHeight,
    sidebarWidthRef,
    showHeader,
    smallNavHeight,
    toggleSidebar,
  } = useSidebarAnimation();
  const shiftAnim = useRef(new Animated.Value(isOpen ? 1 : 0)).current;
  const isFocused = useIsFocused();
  const isBigHeader = headerHeight === bigNavHeight;

  const translateX = shiftAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 165],
    extrapolate: 'clamp',
  });

  const nameOpacity = sidebarWidthRef.interpolate({
    inputRange: [60, 120, 200],
    outputRange: [0, 0.2, 1],
    extrapolate: 'clamp',
  });

  const renderRightHeader = useMemo(() => {
    if (options?.headerRight) {
      return options.headerRight({ tintColor: undefined });
    }
    if (onBack && !titlesWithoutBackButton.includes(title)) {
      return <Button label="Zurück" icon="chevronLeft" onPress={onBack} />;
    }
    return null;
  }, [options?.headerRight, onBack, title]);

  const renderTitleHeader = useMemo(() => {
    return options?.headerTitle &&
      typeof options?.headerTitle === 'function' ? (
      <View style={{ paddingLeft: 10 }}>
        {options.headerTitle({ children: '' })}
      </View>
    ) : (
      <Text style={styles.titleText}>
        {replacedTitle ? replacedTitle : title}
      </Text>
    );
  }, [options?.headerTitle, title, replacedTitle]);

  const renderLeftHeader = useMemo(() => {
    if (
      isBigHeader &&
      !titlesWithoutLogo.includes(title) &&
      !namesWithoutLogo.includes(name)
    ) {
      return (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Image
            source={require('../../../../assets/neolexon-icon.png')}
            style={{ width: 40, height: 40, marginRight: 8 }}
            resizeMode="contain"
          />
          <Animated.View style={{ opacity: nameOpacity }}>
            <Image
              source={require('../../../../assets/neolexon-name.png')}
              style={{ width: 120, height: 35 }}
              resizeMode="contain"
            />
          </Animated.View>
        </View>
      );
    }

    let headerLeft;
    if (options?.headerLeft && typeof options?.headerLeft === 'function') {
      headerLeft = (
        <View style={styles.leftSideWrapper}>
          {options.headerLeft({ tintColor: undefined })}
        </View>
      );
    }

    return (
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        {headerLeft}
        {options?.headerUpsideDown ? (
          <View style={{ paddingLeft: 20 }}>{renderTitleHeader}</View>
        ) : null}
      </View>
    );
  }, [
    isBigHeader,
    title,
    name,
    options?.headerLeft,
    options?.headerTitle,
    options?.headerUpsideDown,
    nameOpacity,
    renderTitleHeader,
  ]);

  useEffect(() => {
    Animated.timing(shiftAnim, {
      toValue: isOpen ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [isOpen, shiftAnim]);

  useEffect(() => {
    if (isFocused) {
      setAppTitle(title);

      const height =
        titlesWithClosedSidebar.includes(title) ||
        typeof options?.headerTitle === 'function'
          ? smallNavHeight
          : bigNavHeight;

      setHeaderHeight(height);
    }
  }, [isFocused, title, options?.headerTitle]);

  useFocusEffect(
    useCallback(() => {
      setShowChevron(
        titlesWithSidebar.includes(title) || namesWithSidebar.includes(name),
      );
      return () => {
        setShowChevron(false);
      };
    }, [title, name]),
  );

  return (
    <Animated.View
      style={[
        styles.header,
        { height: headerHeight },
        options?.headerUpsideDown && { transform: [{ rotate: '180deg' }] },
        !showHeader && { display: 'none' },
      ]}
    >
      {renderLeftHeader}
      {showChevron ? (
        <Animated.View
          style={[
            styles.headerBodyWrapper,
            {
              transform: [{ translateX }],
            },
          ]}
          pointerEvents="box-none"
        >
          <TouchableOpacity
            onPress={toggleSidebar}
            hitSlop={8}
            accessibilityRole="button"
          >
            <View style={styles.headerCollapseButton}>
              <Icon
                name={isOpen ? 'chevronLeft' : 'chevronRight'}
                size={28}
                color={colors.newUI.therapist.primaryBlue80}
              />
            </View>
          </TouchableOpacity>
        </Animated.View>
      ) : null}
      {!options?.headerUpsideDown && (
        <View style={styles.centeredTitleContainer}>{renderTitleHeader}</View>
      )}
      <View style={{ marginLeft: 'auto' }}>{renderRightHeader}</View>
    </Animated.View>
  );
};
